import React, { useEffect, useState, useCallback } from "react";
import api, { fmtMoney, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Send, Plus, Loader2, Banknote, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export default function UserPayout() {
  const [bal, setBal] = useState(0);
  const [benes, setBenes] = useState([]);
  const [selected, setSelected] = useState(null); // beneficiary id or null(new)
  const [amount, setAmount] = useState("");
  const [form, setForm] = useState({ account_name: "", account_number: "", ifsc: "", bank_name: "" });
  const [save, setSave] = useState(true);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const [d, b] = await Promise.all([api.get("/user/dashboard"), api.get("/user/beneficiaries")]);
    setBal(d.data.wallets.payout);
    setBenes(b.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (amt > bal) return toast.error("Amount exceeds payout wallet balance");
    setLoading(true);
    try {
      const payload = selected
        ? { amount: amt, beneficiary_id: selected }
        : { amount: amt, ...form, ifsc: form.ifsc.toUpperCase(), save_beneficiary: save };
      await api.post("/user/payout", payload);
      toast.success("Payout request submitted for approval");
      setAmount("");
      setForm({ account_name: "", account_number: "", ifsc: "", bank_name: "" });
      setSelected(null);
      await load();
    } catch (err) {
      toast.error(apiErr(err, "Payout failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="payout-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Request payout</h1>
        <p className="text-sm text-slate-500 mt-1">Payout wallet balance: <span className="font-mono-data font-semibold text-emerald-600" data-testid="payout-available-balance">{fmtMoney(bal)}</span></p>
      </div>

      {/* Saved beneficiaries */}
      {benes.length > 0 && (
        <div>
          <Label className="mb-2 block">Saved beneficiaries</Label>
          <div className="flex flex-wrap gap-2">
            {benes.map((b) => (
              <button
                key={b.id}
                onClick={() => setSelected(selected === b.id ? null : b.id)}
                data-testid={`beneficiary-pill-${b.id}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-all ${selected === b.id ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-blue-400"}`}
              >
                <User className="h-3.5 w-3.5" />
                <span className="font-medium">{b.account_name}</span>
                <span className="opacity-70 font-mono-data text-xs">••{String(b.account_number).slice(-4)}</span>
              </button>
            ))}
            <button onClick={() => setSelected(null)} data-testid="new-beneficiary-btn" className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm ${selected === null ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900" : "border-dashed border-slate-300 dark:border-slate-700 text-slate-500"}`}>
              <Plus className="h-3.5 w-3.5" /> New account
            </button>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 sm:p-6 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="amount">Amount (₹)</Label>
          <Input id="amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="h-11 font-mono-data" data-testid="payout-amount-input" required />
        </div>

        {selected === null && (
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100 dark:border-slate-800">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="an">Account holder name</Label>
              <Input id="an" value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} className="h-11" data-testid="payout-account-name-input" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ac">Account number</Label>
              <Input id="ac" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="h-11 font-mono-data" data-testid="payout-account-number-input" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ifsc">IFSC code</Label>
              <Input id="ifsc" value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} placeholder="HDFC0001234" className="h-11 font-mono-data uppercase" data-testid="payout-ifsc-input" required />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="bank">Bank name (optional)</Label>
              <Input id="bank" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="h-11" data-testid="payout-bank-name-input" />
            </div>
            <div className="sm:col-span-2 flex items-center justify-between rounded-xl bg-slate-50 dark:bg-slate-800/50 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"><Banknote className="h-4 w-4" /> Save this account as beneficiary</div>
              <Switch checked={save} onCheckedChange={setSave} data-testid="save-beneficiary-switch" />
            </div>
          </div>
        )}

        <Button type="submit" disabled={loading} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="submit-payout-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Request payout</>}
        </Button>
      </form>
    </div>
  );
}
